import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { XGrid } from '@material-ui/x-grid';
import { Button, Box, Fab, makeStyles } from '@material-ui/core';

import AddIcon from '@material-ui/icons/Add';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';

import RMSDatePicker from 'components/RMSDatePicker/RMSDatePicker';
import TimePicker2 from 'components/RMSTimePicker/TimePicker2';
import Tooltip from 'components/Tooltip';
import RMSTextField from 'components/RMSTextField';
import RMSCheckbox2 from 'components/RMSCheckbox/RMSCheckbox2';
import PrintsSearch from 'components/RMSSearch/components/PrintsSearch';
import Dialog from 'components/RMSDialog';
import PersonLookup2 from 'components/RMSPersonLookup/PersonLookup2';
import Dictionary from 'components/RMSAutoComplete/RMSDictonary';
import BackDatedRecordHistory from 'components/BackDatedRecordHistory';

import formStyles, { gridStyle, Row, ColCard, Fills, RowInner } from 'utils/formStyles';
import { getAddressFromLocation } from 'utils/mapFunctions';

import { handleError } from 'reducers/ErrorReducer';
import { notify } from 'reducers/NotifierReducer';
import { getService } from 'reducers/service';
import {
  getPrePlanDetails,
  getPrePlanInspections,
  getAddressDetails,
  savePrePlanInspection,
  delPrePlanInspection,
} from 'reducers/AddressReducer';
import { getCurrentDate, displayDateTime } from 'reducers/TimeReducer';

const useStyles = makeStyles((theme) => ({
  ...formStyles,
  wrap: {
    padding: theme.spacing(3),
  },
  field: gridStyle(210, 400),
  longField: gridStyle(220, '100%'),
  btn: {
    marginLeft: '5px',
    '& svg': {
      marginRight: theme.spacing(1),
    },
  },
  gridWrap: {
    width: '100%',
    boxSizing: 'border-box',
    height: 350,
    marginTop: 32,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(3),
    '& > div': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  gridActions: {
    '& button': {
      marginRight: theme.spacing(1),
    },
  },
}));

const columns = [
  {
    field: 'InspectionDate',
    width: 200,
    valueFormatter: (params) => displayDateTime(params.value),
  },
  'PersonID',
  'FullName',
  'ShiftDesignation',
  {
    field: 'Created',
    headerName: 'Created',
    width: 200,
    valueFormatter: (params) => displayDateTime(params.value),
  },
  { field: 'CreatedBy', headerName: 'Created By', width: 150 },
  {
    field: 'Updated',
    headerName: 'Updated',
    width: 200,
    valueFormatter: (params) => displayDateTime(params.value),
  },
  { field: 'UpdatedBy', headerName: 'Updated By', width: 150 },
];

const AddressPrePlan = (props) => {
  const classes = useStyles();
  const { dictionary, ptsAddressID } = props;
  const {
    codePrePlanConstructionTypes,
    codePrePlanOccupancyTypes,
    codePrePlanRoofTypes,
  } = dictionary;

  const [origDate, setOrigDate] = useState(null);
  const [buildingCapacity, setBuildingCapacity] = useState(0);
  const [numberOfFloors, setNumberOfFloors] = useState(0);
  const [numberOfExits, setNumberOfExits] = useState(0);
  const [firewalls, setFirewalls] = useState(false);
  const [gasService, setGasService] = useState(false);
  const [gasCutoffLocation, setGasCutoffLocation] = useState('');
  const [electricalService, setElectricalService] = useState(false);
  const [electricalCutoffService, setElectricalCutoffService] = useState('');
  const [requiredFlow, setRequiredFlow] = useState(0);
  const [availableFlow, setAvailableFlow] = useState(0);
  const [sprinkler, setSprinkler] = useState(false);
  const [sprinklerShutoffLocation, setSprinklerShutoffLocation] = useState('');
  const [sprinklerFdConnectionLocation, setSprinklerFdConnectionLocation] = useState('');
  const [standpipe, setStandpipe] = useState(false);
  const [standpipeShutoffLocation, setStandpipeShutoffLocation] = useState('');
  const [standpipeFdConnectionLocation, setStandpipeFdConnectionLocation] = useState('');
  const [occupancyType, setOccupancyType] = useState(null);
  const [roofType, setRoofType] = useState(null);
  const [constructionType, setConstructionType] = useState(null);
  const [data, setData] = useState(null);
  const [fullAddress, setFullAddress] = useState('');
  const [AddressID, setAddressID] = useState('');
  const [Inspections, setInspections] = useState([]);
  const [inspectionsLoaded, setInspectionsLoaded] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [selection, setSelection] = useState(null);
  const [editInspectionData, setEditInspectionData] = useState(null);
  const [Notes, setNotes] = useState('');

  const [recordHistoryData, setRecordHistoryData] = useState({
    shouldVisible: true,
    isDisabled: true,
    createUpdateInfo: {},
  });
  const [showRecordHistory, setShowRecordHistory] = useState(false);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    getPrePlanData();
    getAddress();
    getPrePlanInspectionsData();
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!data) return;

    setRecordHistoryData({
      shouldVisible: true,
      isDisabled: !data.Created,
      createUpdateInfo: {
        created: data.Created,
        createdBy: data.CreatedBy,
        updated: data.Updated,
        updatedBy: data.UpdatedBy,
      },
    });

    setOrigDate(data.OrigPrePlanDate || null);
    setBuildingCapacity(data.BuildingCapacity || 0);
    setNumberOfFloors(data.NumberFloors || 0);
    setNumberOfExits(data.NumberOfExits || 0);
    setFirewalls(data.Firewalls || false);
    setGasService(data.GasService || false);
    setGasCutoffLocation(data.GasCutoffLocation || '');
    setElectricalService(data.ElectricalService || false);
    setElectricalCutoffService(data.ElectricalCutoffLocation || '');
    setRequiredFlow(data.RequiredFlow || 0);
    setAvailableFlow(data.AvailableFlow || 0);
    setSprinkler(data.Sprinkler || false);
    setSprinklerShutoffLocation(data.SprinklerShutoffLocation || '');
    setSprinklerFdConnectionLocation(data.SprinklerFDConnectionLocation || '');
    setStandpipe(data.Standpipe || false);
    setStandpipeShutoffLocation(data.StandpipeShutoffLocation || '');
    setStandpipeFdConnectionLocation(data.StandPipeFDConnectionLocation || '');
    setOccupancyType(codePrePlanOccupancyTypes.find((p) => p.Code === data.OccupancyType) || null);
    setRoofType(codePrePlanRoofTypes.find((p) => p.Code === data.RoofType) || null);
    setConstructionType(
      codePrePlanConstructionTypes.find((p) => p.Code === data.ConstructionType) || null
    );
    setNotes(data.Notes || '');
  }, [data]);

  const getAddress = async () => {
    try {
      const addressData = await getAddressDetails(ptsAddressID);
      if (!mountedRef.current || !addressData.data || !addressData.data.length) return;
      const data = addressData.data[0];
      setFullAddress(getAddressFromLocation(data, dictionary));
      setAddressID(data.AddressID);
    } catch (err) {
      props.handleError(err, 'Error receiving event location data.');
    }
  };

  const getPrePlanData = async () => {
    try {
      const data = await getPrePlanDetails(ptsAddressID);
      if (!mountedRef.current || !data) return;
      setData(data);
    } catch (err) {
      props.handleError(err, 'Error receiving address pre plan data.');
    }
  };

  const getPrePlanInspectionsData = async () => {
    try {
      const data = await getPrePlanInspections(ptsAddressID);
      if (!mountedRef.current || !data) return;
      setInspections(data);
      setInspectionsLoaded(true);
    } catch (err) {
      props.handleError(err, 'Error receiving address inspections data.');
    }
  };

  const handleSave = async () => {
    const prePlan = getPrePlanObj();
    const service = getService('cad-address-pre-plan');
    try {
      await service.create(prePlan);
      await getPrePlanData();
      props.notify('Pre plan saved', 'success');
    } catch (err) {
      props.handleError(err, 'Error saving pre plan');
    }
  };

  const handleCloseRecordHistoryDialog = () => setShowRecordHistory(false);

  const getPrePlanObj = () => {
    return {
      OrigPrePlanDate: origDate || null,
      BuildingCapacity: buildingCapacity || null,
      NumberFloors: numberOfFloors || null,
      NumberOfExits: numberOfExits || null,
      Firewalls: firewalls === true ? 1 : 0,
      GasService: gasService === true ? 1 : 0,
      GasCutoffLocation: gasCutoffLocation || null,
      ElectricalService: electricalService === true ? 1 : 0,
      ElectricalCutoffLocation: electricalCutoffService || null,
      RequiredFlow: requiredFlow || null,
      AvailableFlow: availableFlow || null,
      Sprinkler: sprinkler === true ? 1 : 0,
      SprinklerShutoffLocation: sprinklerShutoffLocation || null,
      SprinklerFDConnectionLocation: sprinklerFdConnectionLocation || null,
      Standpipe: standpipe === true ? 1 : 0,
      StandpipeShutoffLocation: standpipeShutoffLocation || null,
      StandpipeFDConnectionLocation: standpipeFdConnectionLocation || null,
      OccupancyType: occupancyType?.Code || null,
      RoofType: roofType?.Code || null,
      ConstructionType: constructionType?.Code || null,
      ptsAddressID,
      Notes: Notes || null,
    };
  };

  const renderOrigPrePlanDate = () => {
    return (
      <RMSDatePicker
        onChange={(date) => setOrigDate(date)}
        label="Original Pre Plan Date"
        value={origDate}
        className={classes.field}
      />
    );
  };

  const renderOccupancyType = () => {
    const onChange = (ev, val) => setOccupancyType(val);
    return (
      <Dictionary
        options="codePrePlanOccupancyTypes"
        className={classes.field}
        onChange={onChange}
        formType="add"
        value={occupancyType}
        label="Occupancy Type"
      />
    );
  };

  const renderConstructionType = () => {
    const onChange = (ev, val) => setConstructionType(val);
    return (
      <Dictionary
        options="codePrePlanConstructionTypes"
        className={classes.field}
        onChange={onChange}
        formType="add"
        value={constructionType}
        label="Construction Type"
      />
    );
  };

  const renderRoofType = () => {
    const onChange = (ev, val) => setRoofType(val);
    return (
      <Dictionary
        options="codePrePlanRoofTypes"
        className={classes.field}
        onChange={onChange}
        formType="add"
        value={roofType}
        label="Roof Type"
      />
    );
  };

  const renderBuildingCapacity = () => {
    const onChange = (ev, val) => setBuildingCapacity(val);
    return (
      <RMSTextField
        className={classes.field}
        label="Building Capacity"
        value={buildingCapacity}
        onChange={onChange}
        type="number"
      />
    );
  };

  const renderNotes = () => {
    const onChange = (ev, val) => setNotes(val);
    return (
      <RMSTextField className={classes.w100pr} label="Notes" value={Notes} onChange={onChange} />
    );
  };

  const renderNumberOfFloors = () => {
    const onChange = (ev, val) => setNumberOfFloors(val);
    return (
      <RMSTextField
        className={classes.field}
        label="Number Of Floors"
        value={numberOfFloors}
        onChange={onChange}
        type="number"
      />
    );
  };

  const renderNumberOfExits = () => {
    const onChange = (ev, val) => setNumberOfExits(val);
    return (
      <RMSTextField
        className={classes.field}
        label="Number Of Exits"
        value={numberOfExits}
        onChange={onChange}
        type="number"
      />
    );
  };

  const renderRequiredFlow = () => {
    const onChange = (ev, val) => setRequiredFlow(val);
    return (
      <RMSTextField
        className={classes.field}
        label="Required Flow"
        value={requiredFlow}
        onChange={onChange}
        type="number"
      />
    );
  };

  const renderAvailableFlow = () => {
    const onChange = (ev, val) => setAvailableFlow(val);
    return (
      <RMSTextField
        className={classes.field}
        label="Available Flow"
        value={availableFlow}
        onChange={onChange}
        type="number"
      />
    );
  };

  const renderFirewalls = () => {
    const onChange = () => setFirewalls(!firewalls);
    return <RMSCheckbox2 checked={firewalls} onChange={onChange} label="Firewalls" />;
  };

  const renderGasService = () => {
    const onChange = () => setGasService(!gasService);
    return <RMSCheckbox2 checked={gasService} onChange={onChange} label="Gas Service" />;
  };

  const renderGasCutoffLocation = () => {
    const onChange = (ev, val) => setGasCutoffLocation(val);
    return (
      <RMSTextField
        className={classes.longField}
        label="Gas Cutoff Location"
        value={gasCutoffLocation}
        onChange={onChange}
        max={100}
      />
    );
  };

  const renderElectricalService = () => {
    const onChange = () => setElectricalService(!electricalService);
    return (
      <RMSCheckbox2 checked={electricalService} onChange={onChange} label="Electrical Service" />
    );
  };

  const renderElectricalCutoffService = () => {
    const onChange = (ev, val) => setElectricalCutoffService(val);
    return (
      <RMSTextField
        className={classes.longField}
        label="Electrical Cutoff Service"
        value={electricalCutoffService}
        onChange={onChange}
        max={100}
      />
    );
  };

  const renderSprinkler = () => {
    const onChange = () => setSprinkler(!sprinkler);
    return <RMSCheckbox2 checked={sprinkler} onChange={onChange} label="Sprinkler" />;
  };

  const renderSprinklerShutoffLocation = () => {
    const onChange = (ev, val) => setSprinklerShutoffLocation(val);
    return (
      <RMSTextField
        className={classes.longField}
        label="Sprinkler Shutoff Location"
        value={sprinklerShutoffLocation}
        onChange={onChange}
        max={100}
      />
    );
  };

  const renderSprinklerFdConnectionLocation = () => {
    const onChange = (ev, val) => setSprinklerFdConnectionLocation(val);
    return (
      <RMSTextField
        className={classes.longField}
        label="Sprinkler Fire Department Connection Location"
        value={sprinklerFdConnectionLocation}
        onChange={onChange}
        max={100}
      />
    );
  };

  const renderStandpipe = () => {
    const onChange = () => setStandpipe(!standpipe);
    return <RMSCheckbox2 checked={standpipe} onChange={onChange} label="Standpipe" />;
  };

  const renderStandpipeShutoffLocation = () => {
    const onChange = (ev, val) => setStandpipeShutoffLocation(val);
    return (
      <RMSTextField
        className={classes.longField}
        label="Standpipe Shutoff Location"
        value={standpipeShutoffLocation}
        onChange={onChange}
        val={100}
      />
    );
  };

  const renderStandpipeFdConnectionLocation = () => {
    const onChange = (ev, val) => setStandpipeFdConnectionLocation(val);
    return (
      <RMSTextField
        className={classes.longField}
        label="Standpipe Fire Department Connection Location"
        value={standpipeFdConnectionLocation}
        onChange={onChange}
        val={100}
      />
    );
  };

  const renderFormActions = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        {recordHistoryData?.shouldVisible && (
          <Box className="">
            <BackDatedRecordHistory
              showRecordHistory={showRecordHistory}
              setShowRecordHistory={setShowRecordHistory}
              disabled={recordHistoryData?.isDisabled}
              handleCloseRecordHistoryDialog={handleCloseRecordHistoryDialog}
              createUpdateInfo={recordHistoryData?.createUpdateInfo}></BackDatedRecordHistory>
          </Box>
        )}
        <Button
          className={classes.btn}
          color="primary"
          variant="contained"
          autoFocus
          disabled={!props.canSave}
          onClick={handleSave}>
          <SaveIcon /> Save
        </Button>
      </div>
    );
  };

  const addInspection = () => {
    setRecordHistoryData({
      shouldVisible: true,
      isDisabled: true,
      createUpdateInfo: {},
    });

    setEditInspectionData({
      InspectionDateTime: getCurrentDate(),
      ptsPersonID: null,
      ShiftDesignation: null,
      PersonID: null,
      ptsAdddressPrePlanID: data.ptsAddressPrePlanID,
    });
  };

  const deleteInspection = async () => {
    if (!confirm('Are you sure you want to delete this inspection?')) return;
    try {
      await delPrePlanInspection(selection);
      if (!mountedRef.current) return;
      getPrePlanInspectionsData();
    } catch (err) {
      props.handleError(err, 'Error deleting PP inspection.');
    }
  };

  const editInspection = async () => {
    const inspection = Inspections.find((i) => i.id === selection);
    if (!inspection) return;

    setRecordHistoryData({
      shouldVisible: true,
      isDisabled: !inspection.Created,
      createUpdateInfo: {
        created: inspection.Created,
        createdBy: inspection.CreatedBy,
        updated: inspection.Updated,
        updatedBy: inspection.UpdatedBy,
      },
    });

    const { InspectionDate, ptsPersonID, ShiftDesignation, id, ptsAdddressPrePlanID } = inspection;
    setEditInspectionData({
      InspectionDateTime: InspectionDate,
      ptsPersonID,
      ShiftDesignation,
      ptsAdddressPrePlanID,
      ptsAddressPrePlanInspectionID: id,
    });
  };

  const renderInspectionActions = () => {
    if (!data) return <div></div>;
    return (
      <div className={classes.gridActions}>
        <Tooltip title="Add inspection">
          <Fab onClick={addInspection} size="small" color="secondary">
            <AddIcon />
          </Fab>
        </Tooltip>
        <PrintsSearch
          title={`${fullAddress} - Inspections`}
          data={Inspections}
          cols={columns}
          hiddenCols={hiddenColumns}
        />
        {!!selection && (
          <>
            <Tooltip title="Edit Inspection">
              <Fab onClick={editInspection} size="small" color="secondary">
                <EditIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Delete Inspection">
              <Fab onClick={deleteInspection} size="small" color="secondary">
                <DeleteIcon />
              </Fab>
            </Tooltip>
          </>
        )}
      </div>
    );
  };

  const closeEditInspection = () => {
    if (!mountedRef.current) return;
    setEditInspectionData(null);
  };

  const saveInspection = async () => {
    try {
      await savePrePlanInspection(editInspectionData);
      if (!mountedRef.current) return;
      await getPrePlanInspectionsData();
      closeEditInspection();
    } catch (err) {
      props.handleError(err, 'Error saving PP inspection.');
    }
  };

  const renderEditInspectionDialog = () => {
    const { InspectionDateTime, ptsPersonID, ShiftDesignation } = editInspectionData;
    const setDate = (date) =>
      setEditInspectionData({ ...editInspectionData, InspectionDateTime: date });
    const setPerson = (val) =>
      setEditInspectionData({
        ...editInspectionData,
        ptsPersonID: val.ptsPersonID,
        PersonID: val.PersonID,
      });
    const onDesignationChange = (ev, val) => {
      setEditInspectionData({ ...editInspectionData, ShiftDesignation: val.Code });
    };
    const { codeDesignation } = dictionary;
    const DesignationType = ShiftDesignation
      ? codeDesignation.find((d) => d.Code === ShiftDesignation)
      : null;
    const valid = ShiftDesignation && ptsPersonID;
    const renderEditInspectionsActions = () => {
      return (
        <>
          {recordHistoryData?.shouldVisible && (
            <Box className="">
              <BackDatedRecordHistory
                showRecordHistory={showRecordHistory}
                setShowRecordHistory={setShowRecordHistory}
                disabled={recordHistoryData?.isDisabled}
                handleCloseRecordHistoryDialog={handleCloseRecordHistoryDialog}
                createUpdateInfo={recordHistoryData?.createUpdateInfo}></BackDatedRecordHistory>
            </Box>
          )}
          <Button
            color="primary"
            variant="contained"
            autoFocus
            onClick={saveInspection}
            disabled={!valid}>
            <SaveIcon /> Save
          </Button>
          <Button color="primary" onClick={closeEditInspection}>
            <CloseIcon /> Close
          </Button>
        </>
      );
    };

    return (
      <Dialog
        onClose={closeEditInspection}
        title={'Add Inspection'}
        actions={renderEditInspectionsActions()}
        className={classes.dialog}
        draggable={false}>
        <Row>
          <ColCard minWidth={500}>
            <RowInner>
              <RMSDatePicker
                onChange={(ev, date) => setDate(date)}
                label="Inspection Date"
                value={InspectionDateTime}
                className={classes.field}
              />
              <TimePicker2
                onChange={(ev, date) => setDate(date)}
                label="Inspection Time"
                value={InspectionDateTime}
                className={classes.field}
              />
            </RowInner>
            <RowInner>
              <PersonLookup2
                className={classes.w100pr}
                onPersonChange={(val) => setPerson(val)}
                ptsPersonID={editInspectionData.ptsPersonID}
              />
            </RowInner>
            <RowInner>
              <Dictionary
                options="codeDesignation"
                className={classes.w100pr}
                formType="add"
                onChange={onDesignationChange}
                value={DesignationType}
                label="Shift Designation"
              />
            </RowInner>
          </ColCard>
        </Row>
      </Dialog>
    );
  };

  return (
    <div className={classes.wrap}>
      <div className={classes.rowSpaces}>
        <h4 className={classes.w50pr}>{fullAddress}</h4>
        <p>{AddressID}</p>
      </div>
      <Row>
        <ColCard minWidth={500}>
          <RowInner>
            {renderOrigPrePlanDate()}
            {renderOccupancyType()}
            {renderConstructionType()}
            {renderRoofType()}
            {renderBuildingCapacity()}
            {renderNumberOfFloors()}
            {renderNumberOfExits()}
            {renderRequiredFlow()}
            {renderAvailableFlow()}
            <Fills className={classes.field} />
          </RowInner>
          <RowInner>{renderNotes()}</RowInner>
        </ColCard>
        <ColCard minWidth={500}>
          <RowInner>
            {renderFirewalls()}
            {renderGasService()}
            {renderGasCutoffLocation()}
          </RowInner>
          <RowInner>{renderElectricalService()}</RowInner>
          <RowInner>{renderElectricalCutoffService()}</RowInner>
        </ColCard>
      </Row>
      <Row>
        <ColCard minWidth={500}>
          <RowInner>{renderSprinkler()}</RowInner>
          <RowInner>{renderSprinklerShutoffLocation()}</RowInner>
          <RowInner>{renderSprinklerFdConnectionLocation()}</RowInner>
        </ColCard>
        <ColCard minWidth={500}>
          <RowInner>{renderStandpipe()}</RowInner>
          <RowInner>{renderStandpipeShutoffLocation()}</RowInner>
          <RowInner>{renderStandpipeFdConnectionLocation()}</RowInner>
        </ColCard>
      </Row>
      <div className={classes.actions}>
        {renderInspectionActions()}
        {renderFormActions()}
      </div>
      {Boolean(data) && (
        <div className={classes.gridWrap}>
          <XGrid
            columns={columns}
            rows={Inspections}
            loading={!inspectionsLoaded}
            rowHeight={38}
            disableMultipleSelection={true}
            showToolbar
            disableColumnFilter
            onSelectionModelChange={(newSelection) => {
              setSelection(newSelection.selectionModel[0]);
            }}
            onColumnVisibilityChange={(col) => setHiddenColumns([...hiddenColumns, col.field])}
          />
        </div>
      )}
      {editInspectionData !== null && renderEditInspectionDialog()}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    dictionary: state.dictionary,
  };
};

export default connect(mapStateToProps, {
  handleError,
  notify,
})(AddressPrePlan);
